import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { Group } from '@/lib/models/group';
import type { ODataResponse } from '@/lib/models/common';

/**
 * Interfaccia per l'endpoint dei Groups
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 */
export interface GroupEndpoints {
	/**
	 * Recupera la lista dei gruppi con query OData
	 * @param query - Query string OData
	 * @returns Promise con response OData contenente array di Group
	 */
	getGroups(query: string): Promise<FetchResponse<ODataResponse<Group>>>;

	/**
	 * Recupera un singolo gruppo per ID
	 * @param id - ID del gruppo
	 * @returns Promise con singolo Group
	 */
	getGroup(id: number): Promise<FetchResponse<Group>>;

	/**
	 * Aggiorna un gruppo esistente
	 * @param id - ID del gruppo da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con Group aggiornato
	 */
	updateGroup(id: number, payload: Group): Promise<FetchResponse<Group>>;

	/**
	 * Crea un nuovo gruppo
	 * @param payload - Dati del nuovo gruppo
	 * @returns Promise con Group creato
	 */
	postGroup(payload: Group): Promise<FetchResponse<Group>>;

	/**
	 * Elimina un gruppo
	 * @param id - ID del gruppo da eliminare
	 * @returns Promise con response di conferma
	 */
	deleteGroup(id: number): Promise<FetchResponse<void>>;

	/**
	 * Recupera i lavoratori di un gruppo (con expand)
	 * @param groupId - ID del gruppo
	 * @returns Promise con response contenente GroupWorkers con expand
	 */
	getWorkers(groupId: number): Promise<FetchResponse<ODataResponse<any>>>;
}

/**
 * Implementazione tipizzata degli endpoint per i Groups
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 */
const groupEndpoints: GroupEndpoints = {
	getGroups(query: string): Promise<FetchResponse<ODataResponse<Group>>> {
		return api.get(`/odata/v1/Groups${query}`);
	},

	getGroup(id: number): Promise<FetchResponse<Group>> {
		return api.get(`/odata/v1/Groups/${id}`);
	},

	updateGroup(id: number, payload: Group): Promise<FetchResponse<Group>> {
		return api.put(`/api/v1/Groups/${id}`, payload);
	},

	postGroup(payload: Group): Promise<FetchResponse<Group>> {
		return api.post('/api/v1/Groups', payload);
	},

	deleteGroup(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/Groups/${id}`);
	},

	getWorkers(groupId: number): Promise<FetchResponse<ODataResponse<any>>> {
		return api.get(
			`/odata/v1/GroupWorkers?$expand=worker($expand=user)&$filter=(groupId eq ${groupId})`,
		);
	},
};

export default groupEndpoints;
